/**
 * Students entity config — drives EntityTable and EntityDetailShell.
 *
 * Billing columns (payment_status, remaining_classes) are implemented via
 * the billing_summary denormalisation approved in 11E.3.
 * billing_summary is refreshed as a side-effect by billing, entitlements,
 * attendance, and bookings mutations — zero extra reads at list time.
 */

import React from 'react';
import { StatusBadge } from '@/components/common/Badge';
import type {
  EntityTableConfig,
  EntityDetailConfig,
} from '@/components/entity/types';
import type { StudentResponse } from '@features/people/api/students';
import { getFullName } from '@/utils';
import {
  StudentCell,
  TagsCell,
  PaymentStatusCell,
  RemainingClassesCell,
} from './studentsEntityComponents';

// ── EntityTableConfig ────────────────────────────────────────────────────

export const studentsTableConfig: EntityTableConfig<StudentResponse> = {
  entityName: 'student',
  apiPath: '/v1/students',
  routeBase: '/students',
  testIdPrefix: 'candeza-student',
  defaultSort: 'created_at',
  defaultOrder: 'desc',
  defaultFilters: { status: 'active' },

  columns: [
    {
      key: 'name',
      header: 'Student',
      isPrimary: true,
      sortable: true,
      helpKey: 'students.name',
      render: (s) => <StudentCell student={s} />,
    },
    {
      key: 'tags',
      header: 'Tags',
      hideOnMobile: true,
      helpKey: 'students.tags',
      render: (s) => <TagsCell student={s} />,
    },
    {
      key: 'created_at',
      header: 'Enrolled',
      sortable: true,
      hideOnMobile: false,
      helpKey: 'students.enrolled',
      render: (s) =>
        new Date(s.created_at).toLocaleDateString('en-IN', {
          day: '2-digit',
          month: 'short',
          year: 'numeric',
        }),
    },
    {
      key: 'payment_status',
      header: 'Payment',
      hideOnMobile: true,
      helpKey: 'students.payment_status',
      render: (s) => <PaymentStatusCell student={s} />,
    },
    {
      key: 'remaining_classes',
      header: 'Remaining',
      hideOnMobile: true,
      helpKey: 'students.remaining_classes',
      render: (s) => <RemainingClassesCell student={s} />,
    },
    {
      key: 'status',
      header: 'Status',
      sortable: true,
      helpKey: 'students.status',
      render: (s) => <StatusBadge status={s.status} />,
    },
  ],

  filters: [
    {
      key: 'status',
      label: 'Status',
      type: 'select',
      helpKey: 'students.filter.status',
      options: [
        { value: 'all', label: 'All Status' },
        { value: 'active', label: 'Active' },
        { value: 'inactive', label: 'Inactive' },
        { value: 'archived', label: 'Archived' },
      ],
    },
  ],

  rowActions: ['view', 'edit', 'archive'],
};

// ── EntityDetailConfig ───────────────────────────────────────────────────
// NOTE: tabs use render-props injected at the route layer to avoid
// cross-feature imports (People must not import from Catalog/Billing/Bookings).
// The config factory below accepts those render-props as arguments.

interface StudentDetailTabProps {
  renderEntitlementsTab?: (studentId: string) => React.ReactNode;
  renderBillingTab?: (studentId: string) => React.ReactNode;
  renderBookingsTab?: (studentId: string) => React.ReactNode;
  renderAttendanceTab?: (studentId: string) => React.ReactNode;
  renderCalendarTab?: (studentId: string) => React.ReactNode;
  renderOverviewTab: (studentId: string) => React.ReactNode;
  /** 11E.8 — Phase 10B instructor reports surfaced as "Grades" (D5). */
  renderGradesTab?: (studentId: string) => React.ReactNode;
  /** 11F.7 — left rail (identity, insights, family). */
  renderSidebar?: (student: StudentResponse) => React.ReactNode;
}

export function buildStudentDetailConfig(
  props: StudentDetailTabProps,
): EntityDetailConfig<StudentResponse> {
  return {
    entityName: 'student',
    routeBase: '/students',
    entityType: 'students',
    testIdPrefix: 'candeza-student',

    getTitle: (s) => getFullName(s.first_name, s.last_name),
    getSubtitle: (s) => (s.email ? s.email : undefined),
    getStatus: (s) => s.status,
    getPhotoUrl: (s) => s.photo_url,
    renderSidebar: props.renderSidebar,

    tabs: [
      {
        id: 'overview',
        label: 'Overview',
        lazyLoad: false, // always render overview
        render: props.renderOverviewTab,
      },
      {
        id: 'entitlements',
        label: 'Entitlements',
        helpKey: 'students.entitlements',
        render: props.renderEntitlementsTab ?? (() => <p style={{ color: '#64748b' }}>Not wired.</p>),
      },
      {
        id: 'bookings',
        label: 'Bookings',
        render: props.renderBookingsTab ?? (() => <p style={{ color: '#64748b' }}>Not wired.</p>),
      },
      {
        id: 'attendance',
        label: 'Attendance',
        helpKey: 'students.attendance',
        render: props.renderAttendanceTab ?? (() => <p style={{ color: '#64748b' }}>Not wired.</p>),
      },
      {
        id: 'calendar',
        label: 'Calendar',
        render: props.renderCalendarTab ?? (() => <p style={{ color: '#64748b' }}>Not wired.</p>),
      },
      {
        id: 'billing',
        label: 'Billing',
        render: props.renderBillingTab ?? (() => <p style={{ color: '#64748b' }}>Not wired.</p>),
      },
      {
        id: 'grades',
        label: 'Grades',
        render: props.renderGradesTab ?? (() => <p style={{ color: '#64748b' }}>Not wired.</p>),
      },
      {
        id: 'communication',
        label: 'Communication',
        render: () => <p style={{ color: '#64748b' }}>Communication history will appear here.</p>,
      },
    ],
  };
}


